import { createContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useSession } from '@/shared/session';

/**
 * Authentication context.
 *
 * A thin projection of the application {@link useSession} onto the auth-facing
 * contract consumed across the app. With the OAuth2-BFF (token-handler) model the
 * browser never holds tokens — `accessToken` is therefore always `null`; the BFF
 * keeps tokens server-side and authenticates the SPA via the session cookie.
 */

export type AuthStatus = 'authenticated' | 'unauthenticated' | 'loading';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  /** Always `null` — tokens are held server-side by the BFF (not exposed to JS). */
  accessToken: string | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  /** Start sign-in (redirect to the BFF OAuth2 authorize endpoint → Keycloak). */
  login: () => void;
  /** End the session (BFF logout + RP-initiated Keycloak logout). */
  logout: () => void;
}

// Context + provider intentionally co-located (see required project structure);
// the hook lives in useAuth.ts.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const session = useSession();

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session.user
        ? {
            id: session.user.id,
            name: session.user.name,
            email: session.user.email,
            avatarUrl: session.user.avatarUrl ?? undefined,
          }
        : null,
      accessToken: null,
      status: session.status,
      isAuthenticated: session.isAuthenticated,
      login: session.login,
      logout: session.logout,
    }),
    [session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
